import { getSsoAccessToken } from './ssoAccessToken'

export interface OperationRequest {
  operation: string
  parameters: Record<string, unknown>
  requested_by?: string
  idempotency_key?: string
}

export interface OperationResponse {
  status: string
  operation: string
  message: string
}

export interface DataGovernor {
  email: string
  idp: string
  hub: string
  business_object: string
}

export interface DataGovernorsListRequest {
  environment: string
  hub?: string
  business_object?: string
}

export interface DataGovernorsListResponse {
  status: 'ok'
  operation: 'data_governor.list'
  environment: string
  governors: DataGovernor[]
}

export interface DataGovernorMutationRequest {
  email: string
  hub: string
  business_object: string
  environment: string
  ticket_id?: string
  requested_by?: string
}

export interface DataGovernorMutationResponse {
  status: 'applied' | 'unchanged'
  operation: 'data_governor.add' | 'data_governor.remove'
  environment: string
  changed: boolean
  message: string
}

export interface Hub {
  name: string
  friendly_name: string
}

export interface HubsListRequest {
  environment: string
}

export interface HubsListResponse {
  status: 'ok'
  operation: 'data_governor.hubs'
  environment: string
  hubs: Hub[]
}

export interface OperationsCountResponse {
  status: 'ok'
  total: number
}

export interface RetryRequestProcessingStepRequest {
  request_id: string
}

export interface RetryRequestProcessingStepResponse {
  status: 'submitted'
  operation: 'request_processing.retry'
  environment: string
  request_id: string
  message: string
}

export class GovernorApiError extends Error {
  // 401 means the SSO token is missing/invalid; 403 means the caller's role is not allowed.
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'GovernorApiError'
  }
}

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? ''

export async function submitOperation(
  request: OperationRequest,
): Promise<OperationResponse> {
  const response = await fetch(`${apiBaseUrl}/api/v1/operations`, {
    method: 'POST',
    headers: withAuthHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })

  const body = (await response.json()) as OperationResponse | { detail?: string }

  if (!response.ok) {
    throw new Error('detail' in body && body.detail ? body.detail : 'Request failed')
  }

  return body as OperationResponse
}

export async function listDataGovernors(
  request: DataGovernorsListRequest,
): Promise<DataGovernorsListResponse> {
  const searchParams = new URLSearchParams({ environment: request.environment })

  if (request.hub) {
    searchParams.set('hub', request.hub)
  }
  if (request.business_object) {
    searchParams.set('business_object', request.business_object)
  }

  return requestGovernor<DataGovernorsListResponse>(`/governor?${searchParams}`)
}

export async function listHubs(request: HubsListRequest): Promise<HubsListResponse> {
  const searchParams = new URLSearchParams({ environment: request.environment })

  return requestGovernor<HubsListResponse>(`/hubs?${searchParams}`)
}

export async function getOperationsCount(): Promise<OperationsCountResponse> {
  return requestGovernor<OperationsCountResponse>('/operations/count')
}

export function addDataGovernor(
  request: DataGovernorMutationRequest,
): Promise<DataGovernorMutationResponse> {
  return requestGovernor<DataGovernorMutationResponse>('/governor', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
}

export function removeDataGovernor(
  request: DataGovernorMutationRequest,
): Promise<DataGovernorMutationResponse> {
  return requestGovernor<DataGovernorMutationResponse>('/governor', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
}

export function retryRequestProcessingStep(
  request: RetryRequestProcessingStepRequest,
): Promise<RetryRequestProcessingStepResponse> {
  return requestGovernor<RetryRequestProcessingStepResponse>('/request-processing/retry', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
}

// Despite the name (kept to avoid an unrelated rename across the /governor call sites above),
// this helper is a generic JSON request + error-mapping wrapper reused by any backend operation
// route, not just /governor.
async function requestGovernor<Response>(path: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: withAuthHeader(init?.headers),
  })
  const body = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    const detail = isErrorResponse(body) ? body.detail : undefined
    throw new GovernorApiError(detail ?? 'Request failed', response.status)
  }

  return body as Response
}

function withAuthHeader(headers?: HeadersInit): HeadersInit {
  const token = getSsoAccessToken()
  return token ? { ...headers, 'X-SSO-Access-Token': token } : (headers ?? {})
}

function isErrorResponse(body: unknown): body is { detail?: string } {
  return typeof body === 'object' && body !== null && 'detail' in body && typeof body.detail === 'string'
}
